import { mdiLayers } from '@mdi/js';
import React from 'react';
import { useSelector } from 'react-redux';
import { msgid, ngettext, t } from 'ttag';
import { useGetRunningProjectLayerAnalysisRunsQuery } from 'uf-api-rtk/store/Api';
import { getActiveOrganizationKey } from 'uf/app/selectors';
import { parseFullPath } from 'uf/base/dataset';
import { ProjectId } from 'uf/projects';
import tw from 'uf/tailwindcss-classnames';
import CanPerform from 'uf/ui/base/CanPerform/CanPerform';
import Flex from 'uf/ui/base/Flex';
import PaneHeader from 'uf/ui/base/Pane/PaneHeader';
import LayerImportButton from 'uf/ui/layers/LayerImportButton/LayerImportButton';
import LayerManagerButton from 'uf/ui/layers/LayerManager/LayerManagerButton/LayerManagerButton';
import useUserFlag from 'uf/ui/user/useUserFlag';
import { ViewPicker } from 'uf/ui/views/ViewPicker/ViewPicker';
import { ViewId } from 'uf/views';
import styles from './ExploreLayerList.module.css';

interface Props {
  projectId: ProjectId;
  loading: boolean;
  layerCount: number;
  viewId: ViewId;
  onSelectView: (viewId: ViewId) => void;
}

const ExploreLayerListHeader = ({
  layerCount,
  loading,
  projectId,
  viewId,
  onSelectView,
}: Props) => {
  const projectKey = projectId ? parseFullPath(projectId).key : '';
  const namespace = useSelector(getActiveOrganizationKey);
  const { currentData: runningJobs } =
    useGetRunningProjectLayerAnalysisRunsQuery(
      {
        namespace,
        key: projectKey,
      },
      { skip: !projectKey || !namespace },
    );
  const numberOfLayers = ngettext(
    msgid`${layerCount} Layer`,
    `${layerCount} Layers`,
    layerCount,
  );
  // Number of active layer analysis service jobs
  const runningJobCount = runningJobs?.length || 0;
  const tooltip = ngettext(
    msgid`${runningJobCount} layer being processed`,
    `${runningJobCount} layers being processed`,
    runningJobCount,
  );
  const hasSavedViewsFlag = useUserFlag('saved-views');
  return (
    <>
      <PaneHeader
        showSpinner={loading || runningJobCount > 0}
        className={styles.header}
        iconPath={mdiLayers}
        title={t`Layers`}
        titleClassName="tw-min-w-0"
        tooltip={tooltip}>
        <CanPerform verb="layer.import" resources={projectId}>
          {({ disabled, permissionsDetails }) => (
            <LayerImportButton
              projectId={projectId}
              disabled={disabled}
              permissionsDetails={permissionsDetails}
            />
          )}
        </CanPerform>
        <LayerManagerButton
          projectId={projectId}
          getAddRemoveTooltipText={getLayerManagerListItemTooltipText}
        />
      </PaneHeader>
      {hasSavedViewsFlag && (
        <ViewPicker
          className={tw('tw-ml-5', 'tw-space-x-2')}
          projectId={projectId}
          viewId={viewId}
          onSelectViewId={onSelectView}
        />
      )}
      <Flex alignItems="center" className={styles.layerCount}>
        {numberOfLayers}
      </Flex>
    </>
  );
};

export default ExploreLayerListHeader;

function getLayerManagerListItemTooltipText() {
  const addTooltipText = t`Add to layer list`;
  const removeTooltipText = t`Remove from layer list`;
  return { addText: addTooltipText, removeText: removeTooltipText };
}
